import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import AppLayout from "@/components/AppLayout";
import AIInsightsPanel from "@/components/AIInsightsPanel";
import ScopeOfImprovement from "@/components/ScopeOfImprovement";
import RiskRing from "@/components/RiskRing";
import ActivityTimeline from "@/components/ActivityTimeline";
import { useAuth } from "@/lib/auth";
import { useAnimatedNumber } from "@/lib/useAnimatedNumber";
import { Sparkle, ArrowRight, ClipboardText, CheckCircle, Clock, TrendDown } from "@phosphor-icons/react";
import { Link } from "react-router-dom";

function AnimatedStat({ label, value, tone, sub, delay }) {
  const v = useAnimatedNumber(value, 1000);
  return (
    <div className={`bg-white border border-slate-200 rounded-md p-5 lift-on-hover fade-up fade-up-${delay}`}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`text-3xl font-semibold mt-1 ${tone}`} style={{ fontFamily: "Geist" }}>
        {Math.round(v)}
      </div>
      {sub && <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}

export default function ClientDashboard() {
  const { user } = useAuth();
  const [company, setCompany] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [insights, setInsights] = useState(null);
  const [insightsLoading, setInsightsLoading] = useState(true);
  const [insightsRefreshing, setInsightsRefreshing] = useState(false);
  const [scope, setScope] = useState(null);
  const [scopeLoading, setScopeLoading] = useState(true);
  const [scopeRefreshing, setScopeRefreshing] = useState(false);
  const [obs, setObs] = useState([]);

  const refreshInsights = async () => {
    if (!user?.company_id) return;
    setInsightsRefreshing(true);
    try {
      const r = await api.get(`/analytics/ai-insights?company_id=${user.company_id}&refresh=true`);
      setInsights(r.data);
    } finally {
      setInsightsRefreshing(false);
    }
  };

  const refreshScope = async () => {
    if (!user?.company_id) return;
    setScopeRefreshing(true);
    try {
      const r = await api.get(`/analytics/client-improvement?company_id=${user.company_id}&refresh=true`);
      setScope(r.data);
    } finally {
      setScopeRefreshing(false);
    }
  };

  useEffect(() => {
    (async () => {
      if (!user?.company_id) return;
      const [c, m, o] = await Promise.all([
        api.get(`/companies/${user.company_id}`),
        api.get(`/analytics/risk-matrix?company_id=${user.company_id}`),
        api.get(`/observations?company_id=${user.company_id}`),
      ]);
      setCompany(c.data);
      setMetrics(m.data);
      setObs(o.data);
      setInsightsLoading(true);
      setScopeLoading(true);
      // fetch AI insights + scope of improvement in parallel
      Promise.allSettled([
        api.get(`/analytics/ai-insights?company_id=${user.company_id}`),
        api.get(`/analytics/client-improvement?company_id=${user.company_id}`),
      ]).then(([insR, scR]) => {
        if (insR.status === "fulfilled") setInsights(insR.value.data);
        setInsightsLoading(false);
        if (scR.status === "fulfilled") setScope(scR.value.data);
        setScopeLoading(false);
      });
    })();
  }, [user?.company_id]);

  const open = obs.filter((o) => o.status === "Open").length;
  const inprog = obs.filter((o) => o.status === "In-Progress").length;
  const resolved = obs.filter((o) => o.status === "Resolved").length;
  const closed = obs.filter((o) => o.status === "Closed").length;
  const completion = obs.length ? Math.round(((resolved + closed) / obs.length) * 100) : 0;
  const animatedCompletion = useAnimatedNumber(completion, 1200);

  const header = (
    <div className="fade-up">
      <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-blue-50 border border-blue-200 text-[#0B2A5B] text-[11px] mb-1 font-semibold">
        <Sparkle size={11} weight="fill" /> AI Risk Advisor
      </div>
      <h1 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: "Geist" }}>
        {company?.name || "Your Company"}
      </h1>
      <p className="text-sm text-slate-500 mt-0.5">Your audit observations and risk reduction progress.</p>
    </div>
  );

  return (
    <AppLayout header={header}>
      {/* Hero risk + completion */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <div className="bg-gradient-to-br from-white to-blue-50/40 border border-slate-200 rounded-lg p-6 flex flex-col items-center justify-center pop-in">
          <RiskRing
            score={metrics?.residual_risk_score || 0}
            inherent={metrics?.inherent_risk_score || 0}
            reduction={metrics?.reduction_pct || 0}
            size={200}
          />
        </div>

        <div className="lg:col-span-2 space-y-4">
          <div className="bg-[#0B2A5B] text-white border border-[#0B2A5B] rounded-lg p-6 relative overflow-hidden fade-up fade-up-1">
            <div className="absolute top-0 right-0 w-32 h-32 bg-[#A8B86C]/10 rounded-full -mr-12 -mt-12"></div>
            <div className="text-[10px] uppercase tracking-wider text-blue-200 font-semibold flex items-center gap-1.5">
              <TrendDown size={11} weight="fill" /> Your Resolution Progress
            </div>
            <div className="flex items-end gap-3 mt-2">
              <div className="text-5xl font-bold" style={{ fontFamily: "Geist" }}>
                {Math.round(animatedCompletion)}<span className="text-2xl text-blue-200">%</span>
              </div>
              <div className="text-sm text-blue-200 pb-2">{resolved + closed} of {obs.length} resolved</div>
            </div>
            <div className="mt-3 h-2 bg-white/10 rounded-full overflow-hidden">
              <div
                className="h-full bg-[#A8B86C] rounded-full transition-all duration-1000 ease-out"
                style={{ width: `${animatedCompletion}%` }}
              />
            </div>
            <p className="text-xs text-blue-100 mt-3">Every resolution you submit reduces your firm's risk score.</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <AnimatedStat label="Open" value={open} tone="text-red-700" delay={2} />
            <AnimatedStat label="In Progress" value={inprog} tone="text-amber-700" delay={3} />
            <AnimatedStat label="Resolved" value={resolved} tone="text-emerald-700" delay={4} />
            <AnimatedStat label="Closed" value={closed} tone="text-slate-700" delay={5} />
          </div>
        </div>
      </div>

      <div className="mb-6 fade-up fade-up-3">
        <AIInsightsPanel insights={insights} loading={insightsLoading} metrics={metrics} onRefresh={refreshInsights} refreshing={insightsRefreshing} />
      </div>

      <div className="mb-6 fade-up fade-up-3">
        <ScopeOfImprovement data={scope} loading={scopeLoading} onRefresh={refreshScope} refreshing={scopeRefreshing} />
      </div>

      {/* Action CTA */}
      {open + inprog > 0 && (
        <Link
          to="/client/observations"
          className="flex items-center justify-between border border-blue-200 bg-blue-50/50 rounded-lg p-5 hover:bg-blue-50 lift-on-hover mb-6"
          data-testid="goto-observations-cta"
        >
          <div>
            <div className="text-[10px] uppercase tracking-wider text-[#0B2A5B] font-semibold">Action Needed</div>
            <div className="text-base font-semibold text-slate-900 mt-0.5" style={{ fontFamily: "Geist" }}>
              Respond to your audit observations
            </div>
            <p className="text-sm text-slate-600 mt-1">{open + inprog} open item(s) waiting for your resolution.</p>
          </div>
          <div className="flex items-center gap-2 text-[#0B2A5B]">
            <ClipboardText size={20} /> <ArrowRight size={16} />
          </div>
        </Link>
      )}

      {user?.company_id && (
        <div className="bg-white border border-slate-200 rounded-lg p-5 fade-up fade-up-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Live Feed</div>
              <h3 className="text-lg font-semibold mt-0.5" style={{ fontFamily: "Geist" }}>Recent Activity</h3>
            </div>
            <span className="inline-flex items-center gap-1 text-[10px] text-emerald-600 font-semibold">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span> Live
            </span>
          </div>
          <ActivityTimeline companyId={user.company_id} compact />
        </div>
      )}
    </AppLayout>
  );
}
